import { computed, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import Cookies from 'js-cookie'
import { BookingSummary } from '@/services/BookingService.interface'
import { CookieName } from '@/config/cookies'
import { LocaleEnum } from '@/config'
import { useDomainRedirect } from '@/composables/useDomainRedirect'
import {
  getPayActionLabel,
  getReserveActions,
  type ReserveAction,
} from '@/utils/bookingFunnelNavigation'

export function useBookingFunnelNavigation(booking: () => BookingSummary): {
  reserveActions: ComputedRef<ReserveAction[]>
  payActionLabel: ComputedRef<string>
  hasMultipleReserveActions: ComputedRef<boolean>
  openReserveAction: (action: ReserveAction) => Promise<void>
  openDefaultReserveAction: () => Promise<void>
} {
  const { t, n } = useI18n()
  const { handleRedirect } = useDomainRedirect()

  const formatPaymentAmount = (amount: string | number): string => {
    let value = Number(amount)
    // Payment rows are major units (often strings like "250.00").
    // Only cent-based numeric fields should be divided by 100.
    if (typeof amount === 'number' && Number.isInteger(value)) {
      value = value / 100
    }
    return n(value, 'currency')
  }

  const reserveActionLabels = computed(() => ({
    reserve: t('reservations.reserve'),
    payDeposit: t('reservations.payDeposit'),
    payBalance: t('reservations.payBalance'),
    depositHint: t('reservations.reserveActionsDepositHint'),
    paymentLinkTitle: (index: number) => t('reservations.paymentLinkTitle', { n: index }),
    paymentLinkTitleSingle: t('reservations.reserveActionsPaymentLinkTitle'),
    echeanceTitle: (index: number) => t('reservations.echeanceTitle', { n: index }),
    echeanceTitleSingle: t('reservations.echeanceTitleSingle'),
    dueBy: (date: string) => t('reservations.reserveActionsDueBy', { date }),
    payPaymentLink: (amount: string) => t('reservations.payPaymentLink', { amount }),
    payPaymentLinkWithDate: (amount: string, date: string) =>
      t('reservations.payPaymentLinkWithDate', { amount, date }),
    payPaymentLinkInstalment: (instalment: number, amount: string) =>
      t('reservations.payPaymentLinkInstalment', { n: instalment, amount }),
    payPaymentLinkInstalmentWithDate: (
      instalment: number,
      amount: string,
      date: string
    ) =>
      t('reservations.payPaymentLinkInstalmentWithDate', {
        n: instalment,
        amount,
        date,
      }),
  }))

  const reserveActions = computed(() =>
    getReserveActions(booking(), reserveActionLabels.value, formatPaymentAmount)
  )

  const payActionLabel = computed(() =>
    getPayActionLabel(booking(), reserveActionLabels.value, formatPaymentAmount)
  )

  const hasMultipleReserveActions = computed(
    () => reserveActions.value.length > 1
  )

  const enrichPaymentUrl = (url: string): string => {
    const updated = new URL(url, window.location.origin)
    const currentLocale = updated.searchParams.get('locale')
    if (currentLocale === LocaleEnum.EN_GB) {
      updated.searchParams.set('locale', LocaleEnum.EN_US)
    }
    const company = Cookies.get(CookieName.COMPANY)
    if (company) {
      updated.searchParams.set('company', company)
    }
    return updated.toString()
  }

  const openReserveAction = async (action: ReserveAction): Promise<void> => {
    const targetUrl =
      action.kind === 'options' ? action.url : enrichPaymentUrl(action.url)
    await handleRedirect(targetUrl)
  }

  const openDefaultReserveAction = async (): Promise<void> => {
    const actions = reserveActions.value
    if (actions.length === 0) {
      console.error('No reserve action available for booking', booking().reference)
      return
    }
    await openReserveAction(actions[0])
  }

  return {
    reserveActions,
    payActionLabel,
    hasMultipleReserveActions,
    openReserveAction,
    openDefaultReserveAction,
  }
}
